/**
 * TipTap rich-text editor for the Knowledge Base.
 *
 * Phase KB-1. Kitchen-sink extension set:
 *   - StarterKit (headings, lists, bold/italic, code, blockquote, hr)
 *   - Image (paste/drag-drop/insert)
 *   - Link
 *   - Table + row + cell + header
 *   - Task list + task item
 *   - Code block with syntax highlight via lowlight
 *   - Placeholder
 *
 * Exposes: <TipTapEditor value={md} onChange={(md) => ...} authToken={...} />
 *
 * `value` and `onChange` use a small Markdown <-> HTML bridge so the article
 * body persists as Markdown on the backend but the editor renders the HTML.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { Link } from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { createLowlight, common } from "lowlight";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3,
  List, ListOrdered, ListChecks, Quote, Link2, Link2Off, Image as ImageIcon,
  Table as TableIcon, Undo2, Redo2, Minus, Eraser,
} from "lucide-react";

const lowlight = createLowlight(common);

// --- minimal Markdown ⇄ HTML bridge (sufficient for our editor's output) ---
// The editor edits HTML; we persist Markdown. For now we store HTML as-is in
// `body_md` and render with `dangerouslySetInnerHTML`. A future iteration can
// add a true MD parser if portability outside the editor becomes important.

const TOOLBAR_BTN = "h-7 w-7 inline-flex items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed";
const TOOLBAR_BTN_ACTIVE = "h-7 w-7 inline-flex items-center justify-center rounded bg-indigo-100 text-indigo-700";

function ToolbarButton({ onClick, active, disabled, title, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={active ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
    >
      {children}
    </button>
  );
}

export default function TipTapEditor({ value, onChange, authToken, placeholder = "Start writing…" }) {
  const fileInputRef = useRef(null);

  const tokenForImg = useMemo(() => authToken || localStorage.getItem("kippu_token") || "", [authToken]);

  // Rewrite stored relative API URLs into full URLs with ?token=
  const withImgAuth = useCallback((html) => {
    if (!html) return "";
    return html.replace(
      /<img\s+([^>]*?)src=["']\/api\/kb-files\/([\w-]+)["']/g,
      (m, attrs, fid) => `<img ${attrs}src="/api/kb-files/${fid}?token=${encodeURIComponent(tokenForImg)}"`,
    );
  }, [tokenForImg]);

  // Strip the token before persisting
  const withoutImgAuth = useCallback((html) => {
    if (!html) return "";
    return html.replace(
      /<img\s+([^>]*?)src=["']\/api\/kb-files\/([\w-]+)\?token=[^"']+["']/g,
      (m, attrs, fid) => `<img ${attrs}src="/api/kb-files/${fid}"`,
    );
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank", class: "text-indigo-600 underline" },
      }),
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "kb-table" } }),
      TableRow, TableCell, TableHeader,
      TaskList, TaskItem.configure({ nested: true }),
      CodeBlockLowlight.configure({ lowlight }),
    ],
    content: withImgAuth(value || ""),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[400px] focus:outline-none px-4 py-3",
        "data-testid": "tiptap-editor",
      },
    },
    onUpdate({ editor }) {
      // Defer to next tick so we don't setState inside another component's render
      const html = withoutImgAuth(editor.getHTML());
      queueMicrotask(() => onChange?.(html));
    },
  });

  // Update content when external `value` changes (e.g. loaded article)
  useEffect(() => {
    if (!editor) return;
    const next = withImgAuth(value || "");
    if (editor.getHTML() !== next) editor.commands.setContent(next, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Image upload handler — accepts <input type=file>, paste, drag-drop
  const uploadImage = useCallback(async (file) => {
    if (!file || !editor) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/kb-files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${r.data.url}?token=${encodeURIComponent(tokenForImg)}`;
      editor.chain().focus().setImage({ src: fullUrl, alt: r.data.filename }).run();
      toast.success("Image inserted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  }, [editor, tokenForImg]);

  // Paste / drag-drop hooks
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
      if (files.length) {
        e.preventDefault();
        files.forEach(uploadImage);
      }
    };
    const onDrop = (e) => {
      const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
      if (files.length) {
        e.preventDefault();
        files.forEach(uploadImage);
      }
    };
    dom.addEventListener("paste", onPaste);
    dom.addEventListener("drop", onDrop);
    return () => {
      dom.removeEventListener("paste", onPaste);
      dom.removeEventListener("drop", onDrop);
    };
  }, [editor, uploadImage]);

  if (!editor) return <div className="text-sm text-slate-400">Loading editor…</div>;

  const isActive = (name, opts) => editor.isActive(name, opts);
  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl || "https://");
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid="tiptap-wrapper">
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-slate-200 bg-slate-50/60 sticky top-0 z-10">
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                       active={isActive("heading", { level: 1 })} title="H1" testid="tt-h1"><Heading1 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                       active={isActive("heading", { level: 2 })} title="H2" testid="tt-h2"><Heading2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                       active={isActive("heading", { level: 3 })} title="H3" testid="tt-h3"><Heading3 className="w-3.5 h-3.5" /></ToolbarButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()}
                       active={isActive("bold")} title="Bold (⌘B)" testid="tt-bold"><Bold className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()}
                       active={isActive("italic")} title="Italic (⌘I)" testid="tt-italic"><Italic className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()}
                       active={isActive("strike")} title="Strike" testid="tt-strike"><Strikethrough className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()}
                       active={isActive("code")} title="Inline code" testid="tt-code"><Code className="w-3.5 h-3.5" /></ToolbarButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()}
                       active={isActive("bulletList")} title="Bullet list" testid="tt-ul"><List className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()}
                       active={isActive("orderedList")} title="Numbered list" testid="tt-ol"><ListOrdered className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleTaskList().run()}
                       active={isActive("taskList")} title="Task list" testid="tt-task"><ListChecks className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()}
                       active={isActive("blockquote")} title="Quote" testid="tt-quote"><Quote className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                       active={isActive("codeBlock")} title="Code block" testid="tt-codeblock"><Code className="w-3.5 h-3.5" /></ToolbarButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton onClick={setLink} active={isActive("link")} title="Link" testid="tt-link"><Link2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} disabled={!isActive("link")} title="Remove link" testid="tt-unlink"><Link2Off className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => fileInputRef.current?.click()} title="Insert image" testid="tt-image"><ImageIcon className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                       title="Table" testid="tt-table"><TableIcon className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider" testid="tt-hr"><Minus className="w-3.5 h-3.5" /></ToolbarButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid="tt-undo"><Undo2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid="tt-redo"><Redo2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear formatting" testid="tt-clear"><Eraser className="w-3.5 h-3.5" /></ToolbarButton>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadImage(file);
          e.target.value = "";
        }}
        data-testid="tiptap-file-input"
      />
      <EditorContent editor={editor} />
    </div>
  );
}
